<template>
  <!-- Audit plan import, step 3: which column of the document each template field is filled from (US 9404) -->
  <div class="api-map" qid="audit-plan-mapping">
    <div class="api-detect">
      <div class="api-detect__card">
        <span class="api-detect__icon api-detect__icon--violet"><font-awesome-icon icon="folder-open"/></span>
        <span>
          <b>{{ $tc('audit_plan_import.mapping.objectives', result.stats.objectives, { count: result.stats.objectives }) }}</b>
          <small>{{ result.layout.objective_layout }}</small>
        </span>
      </div>
      <div class="api-detect__card">
        <span class="api-detect__icon api-detect__icon--teal"><font-awesome-icon icon="shield-alt"/></span>
        <span>
          <b>{{ $tc('audit_plan_import.mapping.controls', result.stats.controls, { count: result.stats.controls }) }}</b>
          <small>{{ $t('audit_plan_import.mapping.pages', { from: result.document.from, to: result.document.to }) }}</small>
        </span>
      </div>
      <div v-if="result.stats.joined" class="api-detect__card">
        <span class="api-detect__icon api-detect__icon--amber"><font-awesome-icon icon="link"/></span>
        <span>
          <b>{{ $tc('audit_plan_import.mapping.joined', result.stats.joined, { count: result.stats.joined }) }}</b>
          <small>{{ $t('audit_plan_import.mapping.joined_text') }}</small>
        </span>
      </div>
    </div>

    <div class="api-map__table">
      <div class="api-map__row api-map__row--head">
        <span>{{ $t('audit_plan_import.mapping.field') }}</span>
        <span></span>
        <span>{{ $t('audit_plan_import.mapping.column') }}</span>
        <span>{{ $t('audit_plan_import.mapping.values') }}</span>
      </div>

      <!-- Control objectives -->
      <div class="api-map__group">{{ $t('audit_plan_import.mapping.group_objective') }}</div>
      <div class="api-map__row" qid="audit-plan-mapping-objective-name">
        <span class="api-map__source"><b>{{ $t('audit_plan_import.mapping.objective_name') }}<span class="mandatory"> *</span></b></span>
        <font-awesome-icon icon="chevron-left" class="api-map__arrow"/>
        <b-form-select :value="settings.objectiveName" :options="objectiveNameOptions" @change="change({ objectiveName: $event })"></b-form-select>
        <span class="api-map__samples">
          <i v-for="objective in firstObjectives" :key="objective.key">{{ objective.name }}</i>
        </span>
      </div>
      <div class="api-map__row" :class="{ 'is-off': !settings.objectiveDescription }" qid="audit-plan-mapping-objective-description">
        <span class="api-map__source"><b>{{ $t('audit_plan_import.mapping.objective_description') }}</b></span>
        <font-awesome-icon icon="chevron-left" class="api-map__arrow"/>
        <b-form-select :value="settings.objectiveDescription" :options="objectiveDescriptionOptions" @change="change({ objectiveDescription: $event })"></b-form-select>
        <span class="api-map__samples">
          <i v-for="objective in firstObjectives" :key="objective.key">{{ objective.description }}</i>
        </span>
      </div>

      <!-- Controls -->
      <div class="api-map__group">{{ $t('audit_plan_import.mapping.group_control') }}</div>
      <div v-for="field in fields"
           :key="field"
           class="api-map__row"
           :class="{ 'is-off': !settings.fields[field] }"
           :qid="'audit-plan-mapping-' + field"
      >
        <span class="api-map__source">
          <b>{{ $t('audit_plan_import.fields.' + field) }}<span v-if="required(field)" class="mandatory"> *</span></b>
          <small v-if="field === 'reference'">{{ $t('audit_plan_import.mapping.reference_rule') }}</small>
          <small v-if="field === 'data_collection_description'">{{ $t('audit_plan_import.mapping.documentation_rule') }}</small>
        </span>
        <font-awesome-icon icon="chevron-left" class="api-map__arrow"/>
        <span>
          <b-form-select :value="settings.fields[field]" :options="optionsFor(field)" @change="changeField(field, $event)"></b-form-select>
          <b-form-textarea v-if="settings.fields[field] === custom"
                           class="mt-2"
                           :value="(settings.customTexts || {})[field] || ''"
                           rows="2"
                           max-rows="6"
                           :placeholder="$t('audit_plan_import.mapping.custom_placeholder')"
                           :qid="'audit-plan-mapping-custom-' + field"
                           @input="changeCustomText(field, $event)"
          ></b-form-textarea>
          <small v-if="suggested(field) && settings.fields[field] === suggested(field)" class="api-map__why">
            <font-awesome-icon icon="sparkles"/>{{ $t('audit_plan_import.mapping.suggested') }}
          </small>
          <button v-else-if="suggested(field)"
                  type="button"
                  class="api-map__why api-map__suggest"
                  :qid="'audit-plan-mapping-suggest-' + field"
                  @click="changeField(field, suggested(field))"
          >
            <font-awesome-icon icon="sparkles"/>{{ $t('audit_plan_import.mapping.use_suggestion', { column: columnName(suggested(field)) }) }}
          </button>
        </span>
        <span class="api-map__samples">
          <i v-if="settings.fields[field] === custom">{{ (settings.customTexts || {})[field] }}</i>
          <template v-else><i v-for="control in firstControls" :key="control.key">{{ control[field] }}</i></template>
        </span>
      </div>
    </div>

    <div v-if="!settings.fields.description" class="us-note at-defaults__warning" qid="audit-plan-mapping-no-description">
      <font-awesome-icon icon="exclamation-triangle"/>
      <span>{{ $t('audit_plan_import.mapping.no_description') }}</span>
    </div>
    <div v-if="unused.length" class="us-note us-note--info" qid="audit-plan-mapping-unused">
      <font-awesome-icon icon="info-circle"/>
      <span>{{ $t('audit_plan_import.mapping.unused', { columns: unused.map(column => columnName(column.id)).join(', ') }) }}</span>
    </div>
  </div>
</template>

<script>
  import {
    CONTROL_FIELDS, CUSTOM_FIELDS, CUSTOM, EMPTY, NAME_AI, NAME_FIRST_WORDS, REFERENCE_AUTO, REFERENCE_NUMBER,
    OBJECTIVE_TITLE, OBJECTIVE_HEADING, OBJECTIVE_DESCRIPTION, applyMapping, suggestedColumn, unusedColumns
  } from './auditPlanMapping.js';

  export default {
    name: 'ImportMapping',
    props: {
      result: {
        type: Object,
        required: true
      },
      settings: {
        type: Object,
        required: true
      },
      // References and objectives already in the template, for the sample references
      existing: {
        type: Object,
        default: null
      }
    },
    computed: {
      fields() {
        return CONTROL_FIELDS;
      },
      custom() {
        return CUSTOM;
      },
      columnOptions() {
        return (this.result.columns || []).map(column => ({ value: column.id, text: this.columnName(column.id) }));
      },
      emptyOption() {
        return { value: EMPTY, text: this.$t('audit_plan_import.mapping.empty') };
      },
      objectiveNameOptions() {
        return [
          { value: OBJECTIVE_TITLE, text: this.$t('audit_plan_import.mapping.objective_title') },
          { value: OBJECTIVE_HEADING, text: this.$t('audit_plan_import.mapping.objective_heading') }
        ];
      },
      objectiveDescriptionOptions() {
        return [
          { value: OBJECTIVE_DESCRIPTION, text: this.$t('audit_plan_import.mapping.objective_text') },
          { value: OBJECTIVE_HEADING, text: this.$t('audit_plan_import.mapping.objective_heading') },
          { value: OBJECTIVE_TITLE, text: this.$t('audit_plan_import.mapping.objective_title') },
          this.emptyOption
        ];
      },
      mapped() {
        return applyMapping({ objectives: this.result.objectives.slice(0, 2) }, this.settings, this.existing);
      },
      firstObjectives() {
        return this.mapped;
      },
      firstControls() {
        return this.mapped.length ? this.mapped[0].controls.slice(0, 2) : [];
      },
      unused() {
        return unusedColumns(this.result, this.settings);
      }
    },
    methods: {
      optionsFor(field) {
        if (field === 'reference') {
          return [
            { value: REFERENCE_AUTO, text: this.$t('audit_plan_import.mapping.reference_auto') },
            { value: REFERENCE_NUMBER, text: this.$t('audit_plan_import.mapping.reference_number') }
          ].concat(this.columnOptions);
        }
        if (field === 'name') {
          return [
            { value: NAME_AI, text: this.$t('audit_plan_import.mapping.control_name_ai') },
            { value: NAME_FIRST_WORDS, text: this.$t('audit_plan_import.mapping.control_name_description') }
          ].concat(this.columnOptions);
        }
        let options = [this.emptyOption].concat(this.columnOptions);
        if (CUSTOM_FIELDS.indexOf(field) !== -1) {
          options.push({ value: CUSTOM, text: this.$t('audit_plan_import.mapping.custom') });
        }
        return options;
      },
      required(field) {
        return ['reference', 'name', 'description'].indexOf(field) !== -1;
      },
      suggested(field) {
        return suggestedColumn(this.result, field);
      },
      columnName(columnId) {
        let column = (this.result.columns || []).find(item => item.id === columnId) || {};
        return column.label || (column.holds ? this.$t('audit_plan_import.mapping.no_header') + ' – ' + column.holds : this.$t('audit_plan_import.mapping.no_header'));
      },
      changeField(field, source) {
        this.change({ fields: Object.assign({}, this.settings.fields, { [field]: source }) });
      },
      changeCustomText(field, text) {
        this.change({ customTexts: Object.assign({}, this.settings.customTexts, { [field]: text }) });
      },
      change(values) {
        this.$emit('change', Object.assign({}, this.settings, values));
      }
    }
  }
</script>
